import React, { useEffect, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { ArtifactData } from '../ai-chat/aiChatTypes';
import { useArtifacts, simulateArtifactStreaming } from './useArtifacts';
import {
  getContentType,
  getDemoArtifactContent,
  isDemoArtifact,
} from '../../helpers/artifactHelpers';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../telemetry';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useSelector } from 'react-redux';
import { selectChatsDetails, selectActiveChatId } from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';
import { getArtifactParts } from '../ai-chat/aiChatHelpers';

interface ArtifactPreviewProps {
  artifactData: {
    artifactId: string;
    title: string;
    versionId?: string; // For historical artifacts from chat history
    version?: number; // Version number for subtitle display
  };
}

export const ArtifactPreview: React.FC<ArtifactPreviewProps> = ({ artifactData }) => {
  const {
    openExistingArtifact,
    startArtifactStreaming,
    addArtifactData,
    addStreamingContent,
    completeArtifactStreaming,
    artifacts,
    streamingContent,
    isStreamingComplete,
    currentArtifactId,
    setStreamingComplete,
  } = useArtifacts();

  const { get_artifact } = useAiAgentApiFacade();
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const fetchingRef = React.useRef<Set<string>>(new Set());
  const { dreamforceDemo } = useFeatureToggle();

  // Get chat messages to find all versions
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const chatsDetails = useSelector(selectChatsDetails);
  const currentChat = activeChatId && chatsDetails ? chatsDetails[activeChatId]?.data : null;

  const openOrBuildArtifact = useCallback(async () => {
    const existingArtifact = artifacts[artifactData.artifactId];

    if (existingArtifact) {
      // Artifact already exists - open the panel
      openExistingArtifact(artifactData.artifactId, artifactData.versionId);
      return;
    }

    // Check if this is a historical artifact (has versionId)
    if (artifactData.versionId) {
      // Prevent duplicate fetches
      if (fetchingRef.current.has(artifactData.versionId)) {
        return;
      }

      fetchingRef.current.add(artifactData.versionId);

      // Historical artifact from chat history - fetch from server
      setIsLoadingHistorical(true);
      try {
        // Step 1: Find all versions of this artifact in chat history
        const allVersionRefs: Array<{ versionId: string; title: string }> = [];

        if (currentChat?.aiChatDetails?.messages) {
          currentChat.aiChatDetails.messages.forEach((message) => {
            const artifactParts = getArtifactParts(message.content);
            artifactParts.forEach((part) => {
              if (part.artifactId === artifactData.artifactId) {
                allVersionRefs.push({
                  versionId: part.versionId,
                  title: part.title,
                });
              }
            });
          });
        }

        // Step 2: Fetch only the clicked version initially
        const fetchedArtifact = await get_artifact(artifactData.versionId);

        // Detect content type from the code
        const detectedContentType = getContentType(fetchedArtifact.code);

        // Step 3: Create versions array with fetched data for clicked version,
        // and placeholders for other versions (to be lazy loaded)
        const versions = allVersionRefs.map((ref) => {
          if (ref.versionId === fetchedArtifact.id) {
            // This is the version we just fetched - include full data
            return {
              versionId: fetchedArtifact.id,
              title: ref.title.replace(/^"|"$/g, ''), // Remove surrounding quotes
              content: fetchedArtifact.code,
              contentType: detectedContentType,
              createdAt: fetchedArtifact.createdAt,
              version: fetchedArtifact.version,
            };
          } else {
            // Placeholder for other versions - will be lazy loaded when selected
            return {
              versionId: ref.versionId,
              title: ref.title.replace(/^"|"$/g, ''),
              content: '', // Empty - will be fetched when needed
              contentType: detectedContentType, // Assume same type for now
              createdAt: '', // Will be filled when fetched
              version: undefined, // Will be filled when fetched
            };
          }
        });

        // Sort versions by the order they appear in chat (earlier = lower version)
        const sortedVersions = versions.map((v, index) => ({
          ...v,
          version: v.version ?? index + 1, // Assign version numbers based on order
        }));

        // Add to artifacts store
        const artifactDataToStore: ArtifactData = {
          artifactId: artifactData.artifactId,
          versions: sortedVersions,
          currentVersion: fetchedArtifact.id,
        };

        addArtifactData(artifactData.artifactId, artifactDataToStore);
        openExistingArtifact(artifactData.artifactId, artifactData.versionId);
      } catch (error) {
        telemetry.captureError(error);
      } finally {
        setIsLoadingHistorical(false);
        fetchingRef.current.delete(artifactData.versionId!);
      }
      return;
    }

    // For demo/mock artifacts only (requires dreamforceDemo feature flag)
    if (dreamforceDemo && isDemoArtifact(artifactData.artifactId)) {
      // Demo artifact - use simulation
      startArtifactStreaming(artifactData.artifactId);

      // Get the demo content based on artifact ID
      const artifactContent = getDemoArtifactContent(artifactData.artifactId);

      // Start the streaming simulation
      simulateArtifactStreaming(
        artifactData.artifactId,
        artifactContent,
        addStreamingContent, // Accumulate chunks
        () => setStreamingComplete(true),
      );
    }
  }, [
    artifactData.artifactId,
    artifactData.versionId,
    artifacts,
    dreamforceDemo,
    openExistingArtifact,
    startArtifactStreaming,
    addStreamingContent,
    setStreamingComplete,
    get_artifact,
    addArtifactData,
    currentChat,
  ]);

  useEffect(() => {
    if (isStreamingComplete && streamingContent && currentArtifactId === artifactData.artifactId) {
      const detectedContentType = getContentType(streamingContent);

      // Use the ACTUAL accumulated streaming content
      const finalArtifactData: ArtifactData = {
        artifactId: artifactData.artifactId,
        versions: [
          {
            versionId: '1',
            title: artifactData.title,
            content: streamingContent,
            contentType: detectedContentType,
            createdAt: new Date().toISOString(),
            version: 1, // Demo artifacts are always version 1
          },
        ],
        currentVersion: '1',
      };

      addArtifactData(artifactData.artifactId, finalArtifactData);
      completeArtifactStreaming();
    }
  }, [
    isStreamingComplete,
    streamingContent,
    currentArtifactId,
    artifactData.artifactId,
    artifactData.title,
    addArtifactData,
    completeArtifactStreaming,
  ]);

  // Auto-open panel when artifact message appears (only for new artifacts that don't exist yet)
  // For real artifacts, useAgentsChats handles the opening automatically
  useEffect(() => {
    const existingArtifact = artifacts[artifactData.artifactId];

    // Only auto-open for demo artifacts that don't exist yet (requires dreamforceDemo flag)
    if (dreamforceDemo && !existingArtifact && isDemoArtifact(artifactData.artifactId)) {
      openOrBuildArtifact();
    }
  }, [openOrBuildArtifact, artifacts, artifactData.artifactId, dreamforceDemo]);

  const handleViewDetails = () => {
    openOrBuildArtifact();
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '12px',
        padding: 2,
        backgroundColor: 'white',
        width: '100%',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
      }}
      onClick={handleViewDetails}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body">{artifactData.title}</Typography>

          <Box sx={{ mb: '12px' }}>
            <Typography variant="caption" color={colors.grey[700]}>
              Interactive artifact • Version {artifactData.version ?? 1}
            </Typography>
          </Box>

          <Button
            endIconName="ChevronRight"
            onClick={handleViewDetails}
            variant="flat-blue"
            disabled={isLoadingHistorical}
          >
            {isLoadingHistorical ? 'Loading...' : 'View details'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
